import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api, type Notification } from "../api";

const POLL_MS = 12_000;

function describe(n: Notification): { title: string; body: string } {
  const p = n.payload as Record<string, unknown>;
  switch (n.templateCode) {
    case "project_submitted":
      return {
        title: "New project submitted",
        body: `${p["ownerName"]} → ${p["projectTitle"]}`,
      };
    case "status_changed":
      return {
        title: `${p["projectTitle"]}`,
        body: `${p["fromStatus"]} → ${p["toStatus"]}`,
      };
    case "comment_posted":
      return {
        title: `${p["authorName"]} commented`,
        body: String(p["excerpt"] ?? "").slice(0, 120),
      };
    case "vote_cast":
      return p["detail"] === "voter"
        ? {
            title: `Vote on ${p["projectTitle"]}`,
            body: `${p["vote"]} from ${String(p["voterUserId"]).slice(0, 8)}…`,
          }
        : {
            title: `Vote cast on ${p["projectTitle"]}`,
            body: "Open the portal to vote.",
          };
    case "approval_finalized":
      return {
        title: `${p["projectTitle"]}: ${p["decision"]}`,
        body: "Decision finalised.",
      };
    case "voting_window_expiring":
      return {
        title: `${p["projectTitle"]}: voting window closing`,
        body: `${p["hoursRemaining"]}h remaining`,
      };
    default:
      return { title: n.templateCode, body: JSON.stringify(p) };
  }
}

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const load = async () => {
    try {
      const res = await api.get<{
        data: Notification[];
        unreadCount: number;
      }>("/api/notifications");
      setItems(res.data);
      setUnread(res.unreadCount);
    } catch {
      /* unauthenticated etc. — quiet */
    }
  };

  useEffect(() => {
    void load();
    const h = setInterval(load, POLL_MS);
    return () => clearInterval(h);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (id: string) => {
    await api.post(`/api/notifications/${id}/read`);
    void load();
  };
  const markAll = async () => {
    await api.post("/api/notifications/read-all");
    void load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        className="relative inline-flex items-center justify-center w-[34px] h-[34px] rounded-md text-white/55 hover:bg-white/[0.09] hover:text-white transition-colors"
      >
        <Bell size={17} />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-brand-500 text-white text-[10px] font-semibold flex items-center justify-center border border-brand-800">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="fixed left-2 right-2 top-14 md:absolute md:left-auto md:right-0 md:top-12 md:w-[380px] max-h-[70vh] overflow-y-auto bg-white text-ink-700 border border-sand-200 rounded-lg shadow-pop z-40 animate-pop">
          <div className="flex items-center justify-between px-4 py-3 border-b border-sand-100">
            <strong className="text-ink-800 text-sm">Notifications</strong>
            <button
              className="text-xs inline-flex items-center gap-1.5 text-ink-500 hover:text-ink-700 disabled:opacity-50"
              onClick={markAll}
              disabled={unread === 0}
            >
              <CheckCheck size={14} />
              Mark all read
            </button>
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-sm text-ink-500 text-center">
              You're all caught up.
            </div>
          ) : (
            items.map((n) => {
              const { title, body } = describe(n);
              const isUnread = n.status !== "read";
              return (
                <div
                  key={n.id}
                  onClick={() => isUnread && markRead(n.id)}
                  className={
                    "px-4 py-3 border-b last:border-b-0 border-ink-100 " +
                    (isUnread
                      ? "bg-accent/15 cursor-pointer hover:bg-accent/25"
                      : "")
                  }
                >
                  <div className="font-semibold text-sm text-ink-800">
                    {title}
                  </div>
                  <div className="text-xs text-ink-500 mt-0.5">{body}</div>
                  {n.projectId && (
                    <div className="mt-1.5">
                      <Link
                        to={`/projects/${n.projectId}`}
                        className="text-xs text-brand-600 hover:text-brand-800"
                      >
                        Open project →
                      </Link>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
